import React, { useState, useEffect } from 'react';
import yaml from 'js-yaml';
import { useStore } from 'zustand';
import { StoreApi } from 'zustand';
import * as THREE from 'three';

import Slider from '../components/ui/Slider';
import ButtonGroup from '../components/ui/ButtonGroup';
import Button from '../components/ui/Button';
import ColorPicker from '../components/ui/ColorPicker';
import Panel from '../components/ui/Panel';

// --- Type Definitions for the YAML structure ---

interface ControlOption {
  label: string;
  value: string | number;
}

interface ControlConfig {
  type: 'slider' | 'button_group' | 'button' | 'color';
  label: string;
  property: string; // The key in the Zustand store
  action: string; // The name of the action function in the store
  options?: ControlOption[]; // For button_group
  min?: number;   // For slider
  max?: number;   // For slider
  step?: number;  // For slider
}

interface ControlGroup {
  id: string;
  title: string;
  controls: ControlConfig[];
}

interface UiConfig {
  title: string;
  groups?: ControlGroup[];
  controls?: ControlConfig[]; // For backwards compatibility
}


// --- Props for the UniversalUIPage ---

interface UniversalUIPageProps {
  yamlFile: string;
  store: StoreApi<any>;
  meshRef: React.RefObject<THREE.InstancedMesh>;
}

// --- Individual Control Components that use Hooks correctly ---

interface ControlProps {
  control: ControlConfig;
  store: StoreApi<any>;
  meshRef: React.RefObject<THREE.InstancedMesh>;
}

const SliderControl: React.FC<ControlProps> = ({ control, store, meshRef }) => {
  const value = useStore(store, (state) => state[control.property]);
  const actionName = control.action;
  const action = store.getState()[actionName];

  // Define which actions require the mesh object as the first parameter.
  const meshActions = new Set([
    'setTargetSize', 'setTargetSizeY', 'setTargetDepth', 
    'setTargetTrapezoidFactorX', 'setTargetTrapezoidFactorY', 'setTargetTrapezoidFactorZ'
  ]);

  return (
    <Slider
      label={control.label}
      min={control.min ?? 0}
      max={control.max ?? 1}
      step={control.step ?? 0.01}
      value={value}
      onChange={(val) => {
        if (meshActions.has(actionName)) {
            if (meshRef.current) {
                action(meshRef.current, val);
            }
        } else {
            action(val);
        }
      }}
    />
  );
};

const ButtonGroupControl: React.FC<ControlProps> = ({ control, store }) => {
  const value = useStore(store, (state) => state[control.property]);
  const action = store.getState()[control.action];
  return (
    <div>
      <label style={{ display: 'block', marginBottom: '0.5rem', color: 'white' }}>{control.label}</label>
      <ButtonGroup
        options={control.options ?? []}
        value={value}
        onChange={(val) => {
            // This action type does not operate on the mesh, so no change here
            action(val);
        }}
      />
    </div>
  );
};

const ButtonControl: React.FC<ControlProps> = ({ control, store, meshRef }) => {
    const action = store.getState()[control.action];
    return (
        <Button onClick={() => {
            if (meshRef.current) {
                action(meshRef.current);
            }
        }}>
            {control.label}
        </Button>
    );
};

const ColorPickerControl: React.FC<ControlProps> = ({ control, store, meshRef }) => {
    const value = useStore(store, (state) => state[control.property]);
    const action = store.getState()[control.action];
    
    // Define which color actions require the mesh object.
    // In our current setup, none do, but this makes it extensible.
    const meshActions = new Set<string>([/* 'someColorActionThatNeedsTheMesh' */]);

    return (
        <ColorPicker
            label={control.label}
            value={value}
            onChange={(val) => {
                if (meshActions.has(control.action) && meshRef.current) {
                    action(meshRef.current, val);
                } else {
                    action(val);
                }
            }}
        />
    );
};


// --- The Main UI Page Component ---

export const UniversalUIPage: React.FC<UniversalUIPageProps> = ({ yamlFile, store, meshRef }) => {
  const [uiConfig, setUiConfig] = useState<UiConfig | null>(null);

  useEffect(() => {
    const fetchYaml = async () => {
      try {
        const response = await fetch(`/${yamlFile}`);
        const text = await response.text();
        const data = yaml.load(text) as UiConfig;
        setUiConfig(data);
      } catch (error) {
        console.error("Error loading or parsing YAML file:", error);
      }
    };

    fetchYaml();
  }, [yamlFile]);

  if (!uiConfig) {
    return <Panel><div style={{color: 'white'}}>Loading UI...</div></Panel>;
  }

  const renderControl = (control: ControlConfig, index: number) => {
    const key = `${control.property}-${index}`;
    switch (control.type) {
      case 'slider':
        return <SliderControl key={key} control={control} store={store} meshRef={meshRef} />;
      case 'button_group':
        return <ButtonGroupControl key={key} control={control} store={store} meshRef={meshRef} />;
      case 'button':
        return <ButtonControl key={key} control={control} store={store} meshRef={meshRef} />;
      case 'color':
        return <ColorPickerControl key={key} control={control} store={store} meshRef={meshRef} />;
      default:
        return <div key={key}>Unknown control type</div>;
    }
  };

  // Check for the new "groups" structure first, otherwise fall back to the old flat "controls" list
  const hasGroups = uiConfig.groups && uiConfig.groups.length > 0;

  return (
    <>
      <h1 style={{
        color: 'white',
        fontSize: '1.5rem',
        marginBottom: '1.5rem',
        paddingBottom: '0.5rem',
        borderBottom: '1px solid rgba(255, 255, 255, 0.2)',
      }}>
        {uiConfig.title}
      </h1>

      {hasGroups ? (
        // New structure with groups
        uiConfig.groups!.map((group) => (
          <div key={group.id} style={{ marginBottom: '2rem' }}>
            <h2 style={{ color: 'rgba(255, 255, 255, 0.8)', fontSize: '1rem', marginBottom: '1rem' }}>
              {group.title}
            </h2>
            {group.controls && group.controls.map(renderControl)}
          </div>
        ))
      ) : (
        // Old structure with a flat list of controls
        uiConfig.controls ? uiConfig.controls.map(renderControl) : <div style={{color: 'white'}}>No controls defined.</div>
      )}
    </>
  );
};